import type {
  FabricActor,
  FabricPrincipal,
  ModelProvider,
  SubmissionTelemetrySink,
} from "@fabric-harness/sdk";
import {
  appServicePrincipalFromEnv,
  databricksCredentials,
  databricksIdentity,
  fabricPrincipalFor,
  onBehalfOfFromHeaders,
} from "./identity.js";
import { databricksPrincipalTenantId } from "./app-user-authorization.js";
import { createDatabricksNativeClients } from "./native-sdk.js";
import type { DatabricksPrincipal, DatabricksTokenProvider } from "./identity.js";
import type { LakebasePoolFactory } from "./lakebase.js";
import { databricksFoundationModelProvider, registerDatabricksModelProvider } from "./model.js";
import { mlflowTraceExporter } from "./mlflow-tracing.js";
import type { MlflowTraceExporter } from "./mlflow-tracing.js";
import type { DatabricksInferenceMode, DatabricksModelOptions } from "./model.js";
import { databricksPersistence } from "./persistence.js";
import type { DatabricksPersistence, DatabricksPersistenceOptions } from "./persistence.js";
import { databricksTelemetry } from "./telemetry-sink.js";
import type { DatabricksTelemetry } from "./telemetry-sink.js";
import { UcVolumesAttachmentStore } from "./volumes-attachment-store.js";

/**
 * Production preset for agents hosted on Databricks Apps (v2, workstream C5).
 *
 * One call resolves the runtime from the standard app environment: the app's
 * service principal (client-credentials OAuth), official workspace SDK clients and
 * serving-endpoint model provider, Lakebase-backed persistence for sessions +
 * submissions + conversation streams, and submission-keyed telemetry — all
 * running as one governed identity that Unity Catalog enforces.
 *
 * ```ts
 * const app = databricksApp();
 * await startDevServer({ port: Number(process.env.DATABRICKS_APP_PORT ?? 8000), ...(await app.serverOptions()) });
 * ```
 */
export interface DatabricksAppOptions {
  /** Environment source. Default `process.env`. */
  env?: Record<string, string | undefined>;
  /** Acting identity. Default: the app service principal from the environment. */
  principal?: DatabricksPrincipal;
  /** Workspace host. Default `DATABRICKS_HOST`. */
  host?: string;
  /** Default model service or serving endpoint. Defaults to `DATABRICKS_MODEL`. */
  model?: string;
  /** AI Gateway or custom Model Serving routing controls. */
  inference?: Omit<DatabricksModelOptions, "host" | "token" | "defaultModel" | "fetchImpl">;
  /**
   * Lakebase connection. Defaults from the app's database resource
   * environment (`DATABRICKS_LAKEBASE_HOST`/`DATABASE`/`USER`/`ENDPOINT`,
   * falling back to `PGHOST`/`PGDATABASE`/`PGUSER`/`ENDPOINT_NAME`). Workspace
   * OAuth is exchanged for a rotating database credential. Set `false` to
   * run without durable persistence.
   */
  lakebase?:
    | false
    | {
        host?: string;
        database?: string;
        user?: string;
        endpoint?: string;
        port?: number;
        ssl?: boolean | { rejectUnauthorized?: boolean };
        poolFactory?: LakebasePoolFactory;
      };
  /** Unity Catalog principal name, when it differs from the client id. */
  ucPrincipal?: string;
  /** Submission-keyed telemetry into Lakebase. Default: on when Lakebase resolves. */
  telemetry?: boolean;
  /**
   * UC Volume attachment storage. Defaults from DATABRICKS_CATALOG,
   * DATABRICKS_SCHEMA, and DATABRICKS_VOLUME; set false to disable.
   */
  attachments?: false | { catalog?: string; schema?: string; volume?: string; rootPrefix?: string };
  fetchImpl?: typeof fetch;
  /**
   * Static persistence-store loader for self-contained bundles. Ordinary Node
   * projects can omit this and use the optional `@fabric-harness/node` peer.
   */
  loadPersistenceStores?: DatabricksPersistenceOptions["loadStores"];
}

export interface DatabricksAppRuntime {
  identity: DatabricksTokenProvider;
  /** Governed identity the app executes as; stamp into actors/submissions. */
  principal: FabricPrincipal;
  actor: FabricActor;
  modelProvider: ModelProvider;
  persistence?: DatabricksPersistence;
  telemetry?: DatabricksTelemetry;
  attachmentStore?: UcVolumesAttachmentStore;
  /** Export settled submission traces without exposing Fabric's raw protocol transport. */
  mlflowTraceExporter(options: {
    experimentId: string;
    onError?: (context: string, error: unknown) => void;
  }): MlflowTraceExporter;
  /**
   * The durable-store slice of `startDevServer` options (submission store +
   * conversation stream store on Lakebase), with schema ensured. Empty when
   * Lakebase is disabled.
   */
  serverOptions(): Promise<{
    sessionStore?: import("@fabric-harness/sdk").SessionStore;
    submissionStore?: import("@fabric-harness/sdk").AgentSubmissionStore;
    conversationStreamStore?: import("@fabric-harness/sdk").ConversationStreamStore;
    attachmentStore?: import("@fabric-harness/sdk").AttachmentStore;
    telemetry?: SubmissionTelemetrySink;
    scheduler?: {
      identity: () => { tenantId: string; actor: FabricActor };
      leaseStore?: import("@fabric-harness/node").SchedulerLeaseStore;
      catchUp: "skip" | "run-once";
    };
  }>;
  /**
   * Per-request on-behalf-of identity from Databricks Apps forwarded
   * headers, or `undefined` when the request carries no user token.
   */
  onBehalfOf(
    headers: Headers | Record<string, string | string[] | undefined>,
  ): { identity: DatabricksTokenProvider; actor: FabricActor } | undefined;
  close(): Promise<void>;
}

export function databricksApp(options: DatabricksAppOptions = {}): DatabricksAppRuntime {
  registerDatabricksModelProvider();
  const env = options.env ?? (typeof process !== "undefined" ? process.env : {});
  const principal = options.principal ?? appServicePrincipalFromEnv(env);
  if (!principal) {
    throw new Error(
      "databricksApp() could not resolve an identity: pass `principal` or set DATABRICKS_HOST + DATABRICKS_CLIENT_ID + DATABRICKS_CLIENT_SECRET (the Databricks Apps runtime provides these).",
    );
  }
  const host =
    options.host ??
    (principal.kind === "service-principal" || principal.kind === "cli-profile"
      ? principal.host
      : undefined) ??
    (env.DATABRICKS_HOST
      ? env.DATABRICKS_HOST.includes("://")
        ? env.DATABRICKS_HOST
        : `https://${env.DATABRICKS_HOST}`
      : undefined);
  if (!host) {
    throw new Error(
      "databricksApp() requires a workspace host: pass `host`, set DATABRICKS_HOST, or resolve one with databricksHostFromCliProfile().",
    );
  }

  const identity = databricksIdentity(principal);
  const fabricPrincipal = fabricPrincipalFor(principal, {
    asAppPrincipal: true,
    ...(options.ucPrincipal ? { ucPrincipal: options.ucPrincipal } : {}),
  });
  const actor: FabricActor = { principal: fabricPrincipal };
  const schedulerIdentity = {
    tenantId: databricksPrincipalTenantId("app", fabricPrincipal.id),
    actor,
  };
  const native = createDatabricksNativeClients({
    host,
    credentials: databricksCredentials(principal),
    tokenProvider: identity,
    ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
  });
  const model = options.model ?? env.DATABRICKS_MODEL;
  const envMode = parseInferenceMode(env.DATABRICKS_INFERENCE_MODE);
  const modelProvider = databricksFoundationModelProvider({
    host,
    token: identity,
    ...(model ? { defaultModel: model } : {}),
    ...(env.DATABRICKS_AI_GATEWAY_BASE_URL ? { baseUrl: env.DATABRICKS_AI_GATEWAY_BASE_URL } : {}),
    ...(envMode ? { mode: envMode } : {}),
    ...(options.inference ?? {}),
    ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
  });

  let persistence: DatabricksPersistence | undefined;
  if (options.lakebase !== false) {
    const lakebase = options.lakebase === undefined ? {} : options.lakebase;
    const lakebaseHost = lakebase.host ?? env.DATABRICKS_LAKEBASE_HOST ?? env.PGHOST;
    const database = lakebase.database ?? env.DATABRICKS_LAKEBASE_DATABASE ?? env.PGDATABASE;
    const endpoint = lakebase.endpoint ?? env.DATABRICKS_LAKEBASE_ENDPOINT ?? env.ENDPOINT_NAME;
    const user =
      lakebase.user ??
      env.DATABRICKS_LAKEBASE_USER ??
      env.PGUSER ??
      (principal.kind === "service-principal" ? principal.clientId : undefined);
    if (lakebaseHost && database && user && endpoint) {
      persistence = databricksPersistence({
        lakebase: {
          host: lakebaseHost,
          database,
          user,
          endpoint,
          credentialClient: native.postgres,
          ...(lakebase.port !== undefined
            ? { port: lakebase.port }
            : env.PGPORT
              ? { port: Number(env.PGPORT) }
              : {}),
          ...(lakebase.ssl !== undefined ? { ssl: lakebase.ssl } : {}),
          ...(lakebase.poolFactory ? { poolFactory: lakebase.poolFactory } : {}),
        },
        ...(options.loadPersistenceStores ? { loadStores: options.loadPersistenceStores } : {}),
      });
    } else if (options.lakebase !== undefined) {
      throw new Error(
        "databricksApp({ lakebase }) is missing connection details: provide host/database/user/endpoint or the DATABRICKS_LAKEBASE_* (or PG*/ENDPOINT_NAME) environment variables from the app database resource.",
      );
    }
  }

  const telemetry =
    persistence && options.telemetry !== false
      ? databricksTelemetry({ client: persistence.client })
      : undefined;
  const attachmentConfig = options.attachments === false ? undefined : (options.attachments ?? {});
  const attachmentCatalog = attachmentConfig?.catalog ?? env.DATABRICKS_CATALOG;
  const attachmentSchema = attachmentConfig?.schema ?? env.DATABRICKS_SCHEMA;
  const attachmentVolume = attachmentConfig?.volume ?? env.DATABRICKS_VOLUME;
  const attachmentStore =
    attachmentCatalog && attachmentSchema && attachmentVolume
      ? new UcVolumesAttachmentStore({
          client: native.files,
          catalog: attachmentCatalog,
          schema: attachmentSchema,
          volume: attachmentVolume,
          rootPrefix:
            attachmentConfig?.rootPrefix ?? env.DATABRICKS_VOLUME_ROOT ?? "fh-attachments",
          ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
        })
      : undefined;

  return {
    identity,
    principal: fabricPrincipal,
    actor,
    modelProvider,
    ...(persistence ? { persistence } : {}),
    ...(telemetry ? { telemetry } : {}),
    ...(attachmentStore ? { attachmentStore } : {}),
    mlflowTraceExporter: (traceOptions) =>
      mlflowTraceExporter({ client: native.rawProtocol, ...traceOptions }),

    async serverOptions() {
      if (!persistence)
        return {
          scheduler: { identity: () => schedulerIdentity, catchUp: "skip" as const },
          ...(attachmentStore ? { attachmentStore } : {}),
        };
      const { store, submissionStore, conversationStreamStore } = await persistence.connectAll();
      const schedulerLeaseStore = await persistence.connectSchedulerLeaseStore();
      return {
        sessionStore: store,
        submissionStore,
        conversationStreamStore,
        ...(telemetry ? { telemetry: telemetry.sink } : {}),
        ...(attachmentStore ? { attachmentStore } : {}),
        scheduler: {
          identity: () => schedulerIdentity,
          ...(schedulerLeaseStore ? { leaseStore: schedulerLeaseStore } : {}),
          catchUp: "run-once",
        },
      };
    },

    onBehalfOf(headers) {
      const user = onBehalfOfFromHeaders(headers);
      if (!user) return undefined;
      return {
        identity: databricksIdentity(user),
        actor: { principal: fabricPrincipalFor(user), onBehalfOf: user.label ?? "user" },
      };
    },

    async close() {
      await persistence?.close();
    },
  };
}

function parseInferenceMode(value: string | undefined): DatabricksInferenceMode | undefined {
  if (!value) return undefined;
  if (value === "auto" || value === "ai-gateway" || value === "serving-endpoints") return value;
  throw new Error(
    `Invalid DATABRICKS_INFERENCE_MODE "${value}". Use auto, ai-gateway, or serving-endpoints.`,
  );
}
